import { PercentageOutlined, PlusOutlined } from '@ant-design/icons';
import {
  type TableProps,
  Tag,
  Tooltip,
  Input,
  InputNumber,
  Select,
  Button,
} from 'antd';
import React, { type JSX } from 'react';

import { DeactivateAction } from '@client/components/actions/deactivate.action';
import { EditAction } from '@client/components/actions/edit.action';
import { PrivacyTag } from '@client/components/PrivacyTag';
import { StatusTag } from '@client/components/StatusTag';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { SubscriptionAssignments } from '@client/pages/features/SubscriptionAssignments';
import { getTranslatedMessage } from '@client/utils/i18n.util';
import { actionField } from '@client/utils/table/action.util';
import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import {
  ECurrency,
  type ELocales,
  type EStatus,
  type IPrice,
  type IFeature,
  type IMetadata,
  EPrivacyStatus,
  type IUtilityCounter,
  EFeatureType,
  type ISubscription,
} from '@common/types';
import { numberToCurrency, tsToLocaleDateTime } from '@common/utils';

import styles from './features.module.less';

import type { TFilters, TSorts } from '@client/hooks/useTable';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: IFeature[];
  onDelete?: (entity: IFeature) => void;
  onEdit?: (id: string) => void;
  isEditing?: boolean;
  editedFeatures?: Record<string, Partial<IFeature>>;
  editedSubscriptions?: Record<string, ISubscription[]>;
  onFeatureChange?: (id: string, field: keyof IFeature, value: any) => void;
  onAssignClick?: (feature: IFeature) => void;
  assignDisabled?: boolean;
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<IFeature>['columns'] => {
  const {
    locale,
    sortedInfo,
    entities,
    filteredInfo,
    onEdit,
    onDelete,
    isEditing,
    editedFeatures,
    editedSubscriptions,
    onFeatureChange,
    onAssignClick,
    assignDisabled,
  } = props;

  const { cached, updateCached } = columnsCache<IFeature>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
    onEdit,
    onDelete,
    namespace: `features-${isEditing ? 'edit' : 'read'}`,
  });

  if (!isEditing && cached) {
    return cached;
  }

  const t = getTranslatedMessage;

  const actionsColumn = {
    ...actionField(),
    render: (record: IFeature): React.JSX.Element => {
      if (isEditing) {
        return <span style={{ color: 'var(--text-color-secondary)' }}>—</span>;
      }
      return (
        <div>
          <EditAction
            subject={EAbilitySubjects.FEATURE}
            onEdit={onEdit}
            type={'text'}
            entityId={record.id}
          />
          <DeactivateAction<IFeature>
            subject={EAbilitySubjects.FEATURE}
            onDelete={onDelete}
            type={'text'}
            entity={record}
            translateKey={t('feature')}
          />
        </div>
      );
    },
  };

  const columns = [
    indexColumn,
    {
      title: t('feature.form.title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      filterSearch: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'title',
      ) as TColumnFilter<IFeature>),
      ...columnSorter(sortedInfo, 'title', 'title', 1),
      render: (title: string, record: IFeature): JSX.Element => {
        if (isEditing) {
          const value =
            editedFeatures?.[record.id]?.title ?? record.title ?? '';
          return (
            <Input
              size="small"
              value={value}
              onChange={(e) =>
                onFeatureChange?.(record.id, 'title', e.target.value)
              }
            />
          );
        }
        return <Tooltip title={record.description}>{title}</Tooltip>;
      },
    },
    {
      title: t('feature.usage'),
      dataIndex: 'utilityCounter',
      key: 'utilityCounter.counter',
      width: 100,
      concealable: true,
      ...columnSorter(
        sortedInfo,
        'utilityCounter',
        'utilityCounter.counter',
        2,
      ),
      render: (
        utilityCounter: IUtilityCounter,
        record: IFeature,
      ): JSX.Element => {
        if (isEditing) {
          const editedVal = editedFeatures?.[record.id]?.utilityCounter;
          const counterVal =
            editedVal !== undefined
              ? editedVal?.counter
              : (utilityCounter?.counter ?? 0);
          return (
            <InputNumber
              size="small"
              min={0}
              value={counterVal}
              onChange={(val) => {
                const currentCounter = utilityCounter || {
                  counter: 0,
                  duration: { type: 'MONTH', period: 1 },
                };
                onFeatureChange?.(record.id, 'utilityCounter', {
                  ...currentCounter,
                  counter: val ?? 0,
                });
              }}
            />
          );
        }
        return (
          <span>{utilityCounter?.counter ? utilityCounter.counter : '0'}</span>
        );
      },
    },
    {
      title: t('price.title'),
      dataIndex: 'price',
      key: 'price.originalPrice',
      width: 100,
      concealable: true,
      render: (price: IPrice, record: IFeature): JSX.Element => {
        if (isEditing) {
          const editedPrice = editedFeatures?.[record.id]?.price;
          const priceVal =
            editedPrice !== undefined
              ? editedPrice?.originalPrice
              : (price?.originalPrice ?? 0);
          return (
            <InputNumber
              size="small"
              min={0}
              value={priceVal}
              onChange={(val) => {
                const currentPrice = price || {
                  originalPrice: 0,
                  currency: ECurrency.USD,
                  isTrialed: false,
                };
                onFeatureChange?.(record.id, 'price', {
                  ...currentPrice,
                  originalPrice: val ?? 0,
                });
              }}
            />
          );
        }
        return (
          <>
            {price?.originalPrice
              ? numberToCurrency(
                  price.originalPrice,
                  locale,
                  price.currency as ECurrency,
                )
              : 0}
          </>
        );
      },
    },
    {
      title: t('form.description'),
      dataIndex: 'description',
      key: 'description',
      width: 250,
      concealable: true,
      render: (description: string, record: IFeature): JSX.Element => {
        if (isEditing) {
          const value =
            editedFeatures?.[record.id]?.description ??
            record.description ??
            '';
          return (
            <Input
              size="small"
              value={value}
              onChange={(e) =>
                onFeatureChange?.(record.id, 'description', e.target.value)
              }
            />
          );
        }
        return <span>{description}</span>;
      },
    },
    {
      title: t('feature.form.slug'),
      dataIndex: 'slug',
      key: 'slug',
      width: 150,
      concealable: true,
      render: (slug: string, record: IFeature): JSX.Element => {
        if (isEditing) {
          const value = editedFeatures?.[record.id]?.slug ?? record.slug ?? '';
          return (
            <Input
              size="small"
              value={value}
              onChange={(e) =>
                onFeatureChange?.(record.id, 'slug', e.target.value)
              }
            />
          );
        }
        return <span>{slug}</span>;
      },
    },
    {
      title: (
        <Tooltip title={t('price.discount')}>
          <PercentageOutlined />
        </Tooltip>
      ),
      align: 'center',
      dataIndex: 'price',
      key: t('price.discount'),
      width: 100,
      concealable: true,
      render: (price: IPrice): JSX.Element | null =>
        price?.discount ? (
          <>
            {price.discount.type === 'PERCENT'
              ? `${price.discount.value}%`
              : numberToCurrency(
                  price.discount.value,
                  locale,
                  price.currency as ECurrency,
                )}
          </>
        ) : null,
    },
    {
      title: t('form.status'),
      dataIndex: 'status',
      key: 'status',
      width: 250,
      align: 'center',
      concealable: true,
      render: (status: keyof typeof EStatus, record: IFeature): JSX.Element => (
        <div className={styles.extraPanel}>
          <PrivacyTag
            status={record.isPublic ? EPrivacyStatus.PUBLIC : 'PRIVATE'}
          />
          <StatusTag status={status} />
        </div>
      ),
    },
    {
      title: t('form.type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      align: 'center',
      concealable: true,
      render: (type: string, record: IFeature): JSX.Element => {
        if (isEditing) {
          const value =
            editedFeatures?.[record.id]?.type ?? record.type ?? 'BUSINESS';
          return (
            <Select
              size="small"
              style={{ width: 120 }}
              value={value}
              onChange={(val) => onFeatureChange?.(record.id, 'type', val)}
              options={Object.keys(EFeatureType).map((t) => ({
                label: EFeatureType[t as keyof typeof EFeatureType],
                value: t,
              }))}
            />
          );
        }
        return <Tag>{type}</Tag>;
      },
    },
    {
      title: t('feature.form.assignments'),
      key: 'assignments',
      width: 250,
      align: 'center',
      concealable: true,
      render: (_, feature: IFeature) => {
        const edited = editedSubscriptions?.[feature.id];
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <SubscriptionAssignments
              featureId={feature.id}
              editedSubscriptions={edited}
            />
            {isEditing && (
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                disabled={assignDisabled}
                onClick={() => onAssignClick?.(feature)}
              >
                {t('actions.assign')}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: t('form.createdAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.createdAt',
      hidden: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.createdAt',
        (createdAt: IMetadata['createdAt']) => tsToLocaleDateTime(createdAt),
      ) as TColumnFilter<IFeature>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.createdAt', 2),
      render: ({ createdAt }: IMetadata): JSX.Element => (
        <div>{createdAt ? tsToLocaleDateTime(createdAt) : null}</div>
      ),
    },
    {
      title: t('form.updatedAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.updatedAt',
      hidden: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.updatedAt',
        (updatedAt: IMetadata['updatedAt']) => tsToLocaleDateTime(updatedAt),
      ) as TColumnFilter<IFeature>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.updatedAt', 3),
      render: ({ updatedAt }: IMetadata): JSX.Element => (
        <div>{updatedAt ? tsToLocaleDateTime(updatedAt) : null}</div>
      ),
    },
  ];

  if (actionsColumn) columns.push(actionsColumn);

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
